import { useState } from 'react';
import { LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes';
import BackButton from '@/components/ui/BackButton/BackButton';
import Stepper from '@/components/shared/Stepper/Stepper';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext';
import SelectInput from '@/components/functional/SelectInput/SelectInput';
import { useCreateRegion } from '@/api/useRegions';
import { useCreateSchools } from '@/api/useSchools';
import { useCreateClasses } from '@/api/useClasses';
import { useCreateSubjects } from '@/api/useSubjects';

const STEPS = ['region', 'schools', 'classes', 'subjects', 'summary'] as const;

const STEP_LABELS: Record<string, string> = {
  region: 'adminAddRegion.stepRegion',
  schools: 'adminAddRegion.stepSchools',
  classes: 'adminAddRegion.stepClasses',
  subjects: 'adminAddRegion.stepSubjects',
  summary: 'adminAddRegion.stepSummary',
};

const RemoveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 13H5v-2h14v2z" fill="currentColor"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
  </svg>
);

type SchoolEntry = { name: string; tempId: string };
type ClassEntry = { name: string; schoolTempId: string; tempId: string };
type SubjectEntry = { name: string; classTempId: string; tempId: string };

let _counter = 0;
const tempId = () => `tmp-${++_counter}`;

const StepperContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { nextStep, currentStepIndex, step } = useStepperContext();

  const createRegion = useCreateRegion();
  const createSchools = useCreateSchools();
  const createClasses = useCreateClasses();
  const createSubjects = useCreateSubjects();

  const [regionName, setRegionName] = useState('');
  const [schools, setSchools] = useState<SchoolEntry[]>([]);
  const [schoolInput, setSchoolInput] = useState('');
  const [classes, setClasses] = useState<ClassEntry[]>([]);
  const [classInput, setClassInput] = useState('');
  const [classSchoolId, setClassSchoolId] = useState('');
  const [subjects, setSubjects] = useState<SubjectEntry[]>([]);
  const [subjectInput, setSubjectInput] = useState('');
  const [subjectClassId, setSubjectClassId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const totalSteps = STEPS.length;
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;
  const stepLabel = t(STEP_LABELS[step] ?? '');

  const addSchool = () => {
    if (!schoolInput.trim()) return;
    setSchools((prev) => [...prev, { name: schoolInput.trim(), tempId: tempId() }]);
    setSchoolInput('');
  };

  const removeSchool = (id: string) => {
    setSchools((prev) => prev.filter((s) => s.tempId !== id));
    setClasses((prev) => prev.filter((c) => c.schoolTempId !== id));
  };

  const addClass = () => {
    if (!classInput.trim() || !classSchoolId) return;
    setClasses((prev) => [...prev, { name: classInput.trim(), schoolTempId: classSchoolId, tempId: tempId() }]);
    setClassInput('');
  };

  const removeClass = (id: string) => {
    setClasses((prev) => prev.filter((c) => c.tempId !== id));
    setSubjects((prev) => prev.filter((s) => s.classTempId !== id));
  };

  const addSubject = () => {
    if (!subjectInput.trim() || !subjectClassId) return;
    setSubjects((prev) => [...prev, { name: subjectInput.trim(), classTempId: subjectClassId, tempId: tempId() }]);
    setSubjectInput('');
  };

  const removeSubject = (id: string) => {
    setSubjects((prev) => prev.filter((s) => s.tempId !== id));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const region = await createRegion.mutateAsync({ name: regionName });

      const schoolMap = new Map<string, string>();
      if (schools.length > 0) {
        const createdSchools = await createSchools.mutateAsync(
          schools.map((s) => ({ name: s.name, region_id: region.id }))
        );
        createdSchools.forEach((created, i) => {
          schoolMap.set(schools[i].tempId, created.id);
        });
      }

      const classMap = new Map<string, string>();
      if (classes.length > 0) {
        const classPayload = classes.map((c) => {
          const schoolId = schoolMap.get(c.schoolTempId);
          if (!schoolId) throw new Error(`Missing school mapping for class "${c.name}"`);
          return { name: c.name, school_id: schoolId };
        });
        const createdClasses = await createClasses.mutateAsync(classPayload);
        createdClasses.forEach((created, i) => {
          classMap.set(classes[i].tempId, created.id);
        });
      }

      if (subjects.length > 0) {
        const subjectPayload = subjects.map((s) => {
          const classId = classMap.get(s.classTempId);
          if (!classId) throw new Error(`Missing class mapping for subject "${s.name}"`);
          return { name: s.name, class_id: classId };
        });
        await createSubjects.mutateAsync(subjectPayload);
      }

      navigate(Routes.ADMIN_DASHBOARD);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSaveError(message);
      console.error('Failed to save region:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const schoolOptions = schools.map((s) => ({ value: s.tempId, label: s.name }));
  const classOptions = classes.map((c) => ({
    value: c.tempId,
    label: `${c.name} (${schools.find((s) => s.tempId === c.schoolTempId)?.name ?? ''})`,
  }));

  return (
    <>
      <div className="mb-6">
        <div className="flex gap-1 mb-1 text-sm">
          <span className="text-[#00000099] font-semibold">{stepLabel}</span>
          <span className="text-[#FF6B6B]">- {currentStepIndex + 1}/{totalSteps}</span>
        </div>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 10,
            borderRadius: 4,
            bgcolor: '#f0f0f0',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#FF6B6B',
              borderRadius: 4,
            },
          }}
        />
      </div>

      {/* Step 1: Region name */}
      <Stepper.Step step="region">
        <div className="flex flex-col gap-4 min-h-[400px]">
          <input
            type="text"
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
            placeholder={t('adminAddRegion.enterRegionName')}
            className="w-full h-[48px] rounded-full px-6 text-sm border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#4E7DF6]"
          />
          <button
            onClick={nextStep}
            disabled={!regionName.trim()}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      </Stepper.Step>

      {/* Step 2: Schools */}
      <Stepper.Step step="schools">
        <div className="flex flex-col gap-4 min-h-[400px]">
          <div className="flex flex-col gap-2">
            {schools.map((school) => (
              <div
                key={school.tempId}
                className="flex items-center justify-between h-[44px] px-5 bg-[#FF6B6B] text-white rounded-full font-semibold text-sm"
              >
                <span>#{schools.indexOf(school) + 1} {school.name}</span>
                <button onClick={() => removeSchool(school.tempId)} className="ml-2 hover:opacity-70">
                  <RemoveIcon />
                </button>
              </div>
            ))}
          </div>

          <input
            type="text"
            value={schoolInput}
            onChange={(e) => setSchoolInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addSchool()}
            placeholder={t('adminAddRegion.enterSchoolName')}
            className="w-full h-[48px] rounded-full px-6 text-sm border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#4E7DF6]"
          />

          <button
            onClick={addSchool}
            className="self-center text-[#4E7DF6] hover:text-[#3D6CE5] transition-colors"
          >
            <PlusIcon />
          </button>

          <button
            onClick={() => { if (schoolInput.trim()) addSchool(); nextStep(); }}
            disabled={schools.length === 0 && !schoolInput.trim()}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      </Stepper.Step>

      {/* Step 3: Classes */}
      <Stepper.Step step="classes">
        <div className="flex flex-col gap-4 min-h-[400px]">
          <div className="flex flex-col gap-2">
            {classes.map((cls) => (
              <div
                key={cls.tempId}
                className="flex items-center justify-between h-[44px] px-5 bg-[#FF6B6B] text-white rounded-full font-semibold text-sm"
              >
                <span>{cls.name} ({schools.find((s) => s.tempId === cls.schoolTempId)?.name})</span>
                <button onClick={() => removeClass(cls.tempId)} className="ml-2 hover:opacity-70">
                  <RemoveIcon />
                </button>
              </div>
            ))}
          </div>

          <SelectInput
            value={classSchoolId}
            onChange={setClassSchoolId}
            options={schoolOptions}
            placeholder={t('adminAddRegion.selectSchool')}
          />

          <input
            type="text"
            value={classInput}
            onChange={(e) => setClassInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addClass()}
            placeholder={t('adminAddRegion.enterClassName')}
            className="w-full h-[48px] rounded-full px-6 text-sm border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#4E7DF6]"
          />

          <button
            onClick={addClass}
            className="self-center text-[#4E7DF6] hover:text-[#3D6CE5] transition-colors"
          >
            <PlusIcon />
          </button>

          <button
            onClick={() => { if (classInput.trim() && classSchoolId) addClass(); nextStep(); }}
            disabled={classes.length === 0 && (!classInput.trim() || !classSchoolId)}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      </Stepper.Step>

      {/* Step 4: Subjects */}
      <Stepper.Step step="subjects">
        <div className="flex flex-col gap-4 min-h-[400px]">
          <div className="flex flex-col gap-2">
            {subjects.map((subj) => (
              <div
                key={subj.tempId}
                className="flex items-center justify-between h-[44px] px-5 bg-[#FF6B6B] text-white rounded-full font-semibold text-sm"
              >
                <span>{subj.name} ({classes.find((c) => c.tempId === subj.classTempId)?.name})</span>
                <button onClick={() => removeSubject(subj.tempId)} className="ml-2 hover:opacity-70">
                  <RemoveIcon />
                </button>
              </div>
            ))}
          </div>

          <SelectInput
            value={subjectClassId}
            onChange={setSubjectClassId}
            options={classOptions}
            placeholder={t('adminAddRegion.selectClass')}
          />

          <input
            type="text"
            value={subjectInput}
            onChange={(e) => setSubjectInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addSubject()}
            placeholder={t('adminAddRegion.enterSubjectName')}
            className="w-full h-[48px] rounded-full px-6 text-sm border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#4E7DF6]"
          />

          <button
            onClick={addSubject}
            className="self-center text-[#4E7DF6] hover:text-[#3D6CE5] transition-colors"
          >
            <PlusIcon />
          </button>

          <button
            onClick={() => { if (subjectInput.trim() && subjectClassId) addSubject(); nextStep(); }}
            disabled={subjects.length === 0 && (!subjectInput.trim() || !subjectClassId)}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      </Stepper.Step>

      {/* Step 5: Summary */}
      <Stepper.Step step="summary">
        <div className="flex flex-col gap-6 min-h-[400px]">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 mb-2">{t('adminAddRegion.summaryRegion')}</h3>
            <p className="text-lg font-bold">{regionName}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-500 mb-2">{t('adminAddRegion.summarySchools')}</h3>
            <div className="flex flex-wrap gap-2">
              {schools.map((s) => (
                <span key={s.tempId} className="px-4 py-2 bg-[#EEF1FE] text-[#4E7DF6] rounded-full text-sm font-medium">
                  {s.name}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-500 mb-2">{t('adminAddRegion.summaryClasses')}</h3>
            <div className="flex flex-wrap gap-2">
              {classes.map((c) => (
                <span key={c.tempId} className="px-4 py-2 bg-[#EEF1FE] text-[#4E7DF6] rounded-full text-sm font-medium">
                  {c.name}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-500 mb-2">{t('adminAddRegion.summarySubjects')}</h3>
            <div className="flex flex-wrap gap-2">
              {subjects.map((s) => (
                <span key={s.tempId} className="px-4 py-2 bg-[#EEF1FE] text-[#4E7DF6] rounded-full text-sm font-medium">
                  {s.name}
                </span>
              ))}
            </div>
          </div>

          {saveError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {saveError}
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full h-[42px] bg-[#4E7DF6] text-white rounded-full font-semibold hover:bg-[#3D6CE5] transition-colors mt-auto flex items-center justify-center disabled:opacity-50"
          >
            {isSaving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              t('button.save')
            )}
          </button>
        </div>
      </Stepper.Step>
    </>
  );
};

const AdminAddRegionPage = () => {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 mb-4">
        <BackButton />
      </div>

      <Stepper.Wrapper steps={STEPS} initialStep="region">
        <StepperContent />
      </Stepper.Wrapper>
    </div>
  );
};

export default AdminAddRegionPage;
